<script setup lang="ts">
//#region imports
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useError } from '@/composables/useError';
import { useApi } from '@/api';
import { type CacheProps, usePopupManagerCache } from '@/composables/usePopupManagerCache';
import { type EmitsToListeners, type ErrorResponse, type ToErrorMap, typeGuard } from '@/types/core';
import type {
  GetMediaBuyingPaymentMethodsBlacklistResponse, GetMediaBuyingPaymentMethodsResponse,
  PostMediaBuyingPaymentMethodsBlacklistData,
  PutMediaBuyingPaymentMethodsBlacklistByIdData,
} from '@/types/openapi-ts';

//#endregion

//#region types
type Form = Pick<
  GetMediaBuyingPaymentMethodsBlacklistResponse['payload'][number],
  'media_buying_payment_method_id' | 'payment_wallet'
>;

type FormErrors = ToErrorMap<
  PostMediaBuyingPaymentMethodsBlacklistData['body'] & PutMediaBuyingPaymentMethodsBlacklistByIdData['body']
>;

interface Props extends CacheProps {
  id?: string;
  data?: Form;
}

interface  Emits {
  'success': [],
  'close': [],
}

export interface BlacklistWalletParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  data: () => ({
    payment_wallet: '',
    media_buying_payment_method_id: '',
  }),
});
const emit = defineEmits<Emits>();
//#endregion

//#region const

//#endregion

//#region composables
const api = useApi();
const { t } = useI18n();
const { getError, errors } = useError<NonNullable<FormErrors>>();

const { fetchData } = usePopupManagerCache(
  props,
  () => api.getMediaBuyingPaymentMethods().then((res) => res.data.payload),
);
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);

const paymentList = ref<GetMediaBuyingPaymentMethodsResponse['payload']>([]);
const data = ref(props.data);
//#endregion

//#region computed
const paymentWalletPlaceholder = computed(() => {
  return paymentList.value.find(
    (el) => el.id === data.value?.media_buying_payment_method_id)?.wallet_placeholder
    || t('mediaBuying.blacklistWallets.popup.placeholderAddress');
});
//#endregion

//#region methods
async function open() {
  isVisible.value = true;

  isLoading.value = true;
  paymentList.value = await fetchData();
  isLoading.value = false;
}

const handleClose = () => {
  emit('close');
  isVisible.value = false;
};

function handleSave() {
  isLoading.value = true;
  const blacklist = {
    media_buying_payment_method_id: data.value.media_buying_payment_method_id,
    payment_wallet: data.value.payment_wallet,
  };

  const promise = props.id
    ? api.putMediaBuyingBlacklist(props.id, blacklist)
    : api.postMediaBuyingBlacklist({ blacklist: [blacklist] });

  promise.then(() => {
    emit('success');
    handleClose();
  }).catch((e) => {
    errors.value = e?.data?.errors || {};
  }).finally(() => isLoading.value = false);
}
//#endregion

defineExpose({
  open,
  fetchData,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="add-blacklist-popup"
    :title="props.id
      ? $t('mediaBuying.blacklistWallets.popup.titleEdit')
      : $t('mediaBuying.blacklistWallets.popup.title')"
    :width="480"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiSelectElement
          v-model="data.media_buying_payment_method_id"
          :label="$t('mediaBuying.blacklistWallets.popup.paymentMethod')"
          :placeholder="$t('mediaBuying.blacklistWallets.popup.placeholderPaymentMethod')"
          :list="paymentList"
          filterable
          size="big"
          :error="getError(['blacklist', 0, 'media_buying_payment_method_id']) || getError('media_buying_payment_method_id')"
        />
        <UiInput
          v-model="data.payment_wallet"
          :label="$t('mediaBuying.blacklistWallets.popup.address')"
          :placeholder="paymentWalletPlaceholder"
          autosize
          size="big"
          :error="getError(['blacklist', 0, 'payment_wallet']) || getError('payment_wallet')"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          :disabled="isLoading"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          :disabled="isLoading"
          @click="handleSave"
        >
          {{ $t('crm.buttons.save') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
